// Index des routes — /trajets|routes|routen (maillage complet)
import { JsonLd, RouteLinks, SeoPage, useHreflang, useSeo } from "@/components/seo/Seo";
import { AIRPORTS, BASE_URL, LANGS, PLACE_NAME, ROUTES, S, T, p } from "@/lib/seoContent";

export const ROUTES_INDEX_COPY = {
  fr: {
    title: "Tous les trajets avec chauffeur en Suisse | Prix fixes | MILES",
    meta: "Toutes les routes MILES : transferts aéroport Genève et Zurich, liaisons interurbaines, stations de ski. Distance, durée et prix fixe affichés avant réservation.",
    h1: "Chaque trajet, son prix avant de monter.",
    intro: "Transferts aéroport, liaisons entre villes, routes vers les stations de ski — chaque trajet affiche sa distance, sa durée et son prix fixe avant la réservation.",
    from_airport: { gva: "Depuis l'aéroport de Genève", zrh: "Depuis l'aéroport de Zurich" },
    from_city: { geneva: "Depuis Genève", zurich: "Depuis Zurich" },
  },
  en: {
    title: "All Chauffeur Routes in Switzerland | Fixed Prices | MILES",
    meta: "Every MILES route: Geneva and Zurich airport transfers, intercity rides, ski resorts. Distance, drive time and fixed price shown before booking.",
    h1: "Every route, priced before you ride.",
    intro: "Airport transfers, city-to-city rides, ski resort routes — every route shows its distance, drive time and fixed price before you book.",
    from_airport: { gva: "From Geneva Airport", zrh: "From Zurich Airport" },
    from_city: { geneva: "From Geneva", zurich: "From Zurich" },
  },
  de: {
    title: "Alle Chauffeur-Routen in der Schweiz | Fixpreise | MILES",
    meta: "Alle MILES-Routen: Flughafentransfers Genf und Zürich, Intercity-Fahrten, Skigebiete. Distanz, Fahrzeit und Fixpreis vor der Buchung sichtbar.",
    h1: "Jede Route, mit Preis vor der Fahrt.",
    intro: "Flughafentransfers, Fahrten zwischen Städten, Routen in die Skigebiete — jede Route zeigt Distanz, Fahrzeit und Fixpreis vor der Buchung.",
    from_airport: { gva: "Ab Flughafen Genf", zrh: "Ab Flughafen Zürich" },
    from_city: { geneva: "Ab Genf", zurich: "Ab Zürich" },
  },
};

export default function RoutesIndex({ lang }) {
  const c = ROUTES_INDEX_COPY[lang];
  const t = T[lang];
  const path = p(lang, S.routes[lang]);
  const alternates = Object.fromEntries(LANGS.map((l) => [l, p(l, S.routes[l])]));
  useSeo({ lang, title: c.title, description: c.meta, path, type: "routes", id: "" });
  useHreflang(alternates);

  const crumbs = [{ label: t.home, to: p(lang) }, { label: t.routes_title }];
  const groups = ["gva", "zrh", "geneva", "zurich"].map((origin) => ({
    title: c.from_airport[origin] || c.from_city[origin],
    origin,
    routes: ROUTES.filter((r) => r.origin === origin),
  }));

  const jsonLd = [
    { "@context": "https://schema.org", "@type": "BreadcrumbList",
      itemListElement: crumbs.map((x, i) => ({ "@type": "ListItem", position: i + 1, name: x.label, ...(x.to ? { item: `${BASE_URL}${x.to}` } : {}) })) },
    { "@context": "https://schema.org", "@type": "ItemList",
      name: c.title,
      itemListElement: ROUTES.map((r, i) => ({
        "@type": "ListItem", position: i + 1,
        name: `${PLACE_NAME[r.origin][lang]} → ${PLACE_NAME[r.destination][lang]}`,
      })) },
  ];

  return (
    <SeoPage lang={lang} breadcrumbs={crumbs} testId="seo-routes-index">
      {jsonLd.map((j, i) => <JsonLd key={i} id={`routes-${i}`} data={j} />)}
      <h1 className="max-w-3xl text-3xl font-bold leading-tight text-ink sm:text-5xl" data-testid="routes-h1">{c.h1}</h1>
      <p className="mt-5 max-w-2xl text-base leading-relaxed text-slate">{c.intro}</p>
      <div className="mt-12 space-y-14">
        {groups.map((g) => (
          <RouteLinks key={g.origin} title={g.title} routes={g.routes} lang={lang} testId={`routes-from-${g.origin}`} />
        ))}
      </div>
    </SeoPage>
  );
}
